import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import EmptyMessage from '@/features/shared/EmptyMessage';
import { useDataLayerSearchEvents } from '@/features/shared/Hooks/data-layer';
import {
  MuiPagination,
  MuiPaginationItem,
  MuiTablePagination,
} from '@/features/shared/material-ui';
import { SearchMeta } from '@/features/shared/SearchMeta';
import { IS_PREVIEW, LoadingStatusValues, SDL_DEFAULT_QPARAMS, Theme } from '@/utils/constants';
import { setPageLink } from '@/utils/helpers';
import { TablePaginationActionsProps } from '@material-ui/core/TablePagination/TablePaginationActions';
import clsx from 'clsx';
import { get, isEmpty } from 'lodash/fp';
import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PrintToolBar from '../PrintToolBar/PrintToolBar';
import { SDLTablePlaceholder } from './SDLtable.placeholder';
import useStyles from './SDLtable.styles';
import SDLpaginatedTable from './shared/PaginatedTable/SDLpaginatedTable';

const ROWS_OPTIONS_DEFAULT = [5, 10, 25];

const SDLtable = ({
  headers = [],
  searchKey,
  theme = Theme.dark,
  labels,
  config,
  isCached,
}: any) => {
  const classes = useStyles({});
  const {
    status,
    queryParams,
    updateQueryParams,
    results = [],
    pagination,
  } = useContext(ElasticComponentContext);
  const { triggerSearchEventDataLayer } = useDataLayerSearchEvents('page_nav_click');
  const handlePagination = (event: any, value: number) => {
    event.preventDefault();
    updateQueryParams('page', String(value));
    triggerSearchEventDataLayer();
  };
  const handleChangeRowsPerPage = (event: any) => {
    updateQueryParams('size', String(parseInt(event.target.value, 10)));
    updateQueryParams('page', '1');
  };
  const [queries, setQueries] = useState(queryParams);
  const rowsOptions = React.useRef(config.rowsPerPageOptions);

  const AnchorPaging = (props: TablePaginationActionsProps) => {
    const handlePageClick = (evt: any, pageNum: number) => {
      evt.preventDefault();
      handlePagination(evt, pageNum);
    };

    return (
      <MuiPagination
        className={clsx(classes.pagination, 'gtm-pagination-container')}
        count={pagination?.totalPages}
        page={pagination?.currentPage}
        onChange={handlePageClick}
        renderItem={(item: any) => (
          <MuiPaginationItem component={Link} to={setPageLink(item.page)} {...item} />
        )}
      />
    );
  };

  useEffect(() => {
    setQueries(queryParams);
  }, [queryParams]);

  const headings = useMemo(
    () =>
      Object.entries(headers).map((head) => {
        return {
          key: head[0],
          value: head[1],
        };
      }),
    [headers],
  );

  const printData = {
    headings,
    results,
  };

  const isLoading = useMemo(
    () => status === LoadingStatusValues.loading && !isCached,
    [isCached, status],
  );

  const hasNoFilters = !Object.keys(queries?.filters || {}).length;
  const matchesDefaults = !Object.keys(SDL_DEFAULT_QPARAMS)
    .filter((p) => p !== 'size')
    .some((p) => `${queries[p]}` !== `${(SDL_DEFAULT_QPARAMS as any)[p]}`);
  const hasDefaultSize = ['10', '100', ''].includes(`${queries.size}`);
  const hasDefaultType = SDL_DEFAULT_QPARAMS.category_type === queries?.category_type;
  const isLanding = hasNoFilters && matchesDefaults && hasDefaultSize;
  const metaConfig = hasDefaultType
    ? { canonParams: { size: 100 }, isLanding }
    : { canonParams: { size: 100, category_type: queries.category_type }, isLanding };

  const rootClasses = clsx(classes.root, `${theme}-theme`);

  if (IS_PREVIEW && !isCached) {
    return (
      <div className={classes.wrapper}>
        <div className={rootClasses}>
          <SDLTablePlaceholder theme={theme} />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      <div className={rootClasses}>
        {!!results?.length && (
          <div className={classes.printButtons}>
            <PrintToolBar
              data={printData}
              searchKey={searchKey}
              queries={queries}
              labels={labels}
              config={config}
              theme={theme}
              deviceType={queries?.category_type}
            />
          </div>
        )}
        <div>
          <SDLpaginatedTable
            data={results}
            headers={headings}
            isLoading={isLoading}
            labels={labels}
            theme={theme}
            deviceType={queries?.category_type}
          />
        </div>

        {status === 'READY' && isEmpty(results) && (
          <EmptyMessage message={get('empty_results_label', labels)} />
        )}

        {!isEmpty(results) && (
          <MuiTablePagination
            data-datalayer-position="sdl_pagination"
            component="div"
            rowsPerPageOptions={rowsOptions.current || ROWS_OPTIONS_DEFAULT}
            className={classes.pagination}
            classes={{ spacer: classes.spacer }}
            rowsPerPage={pagination?.resultsPerPage || 10}
            count={pagination?.totalResults}
            labelDisplayedRows={() => ''}
            page={pagination?.currentPage - 1}
            onChangePage={handlePagination}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            ActionsComponent={AnchorPaging}
            labelRowsPerPage={labels?.rows_per_page || 'Rows per page:'}
          />
        )}
        {status === 'READY' && (
          <SearchMeta paging={pagination} query={queryParams} config={metaConfig} />
        )}
      </div>
    </div>
  );
};

export default memo(SDLtable);
